// Auger purge settings live on the machine. Null means the machine has not
// reported that value yet, which is different from a reported 0.

export type AugerPurgeFieldKey = 'duty_cycle_pct' | 'dispense_time_sec' | 'water_mixer_ml' | 'frequency_cycles';

export interface AugerPurgeField {
	key: AugerPurgeFieldKey;
	label: string;
	summaryLabel: string;
	unit: string;
	kind: 'integer' | 'decimal';
	// Inclusive bounds from the machine cleaning screen's keypad.
	min: number;
	max: number;
	// How far + and - move. Typed values can still be any whole number in range.
	step: number;
	// What to show when the machine has not reported this field. These four keep
	// a factory value until a change is reported. 0 frequency is a real setting:
	// it turns that canister off.
	unreportedLabel: 'Default' | 'Unknown';
}

export const AUGER_PURGE_FIELDS: AugerPurgeField[] = [
	{ key: 'duty_cycle_pct', label: 'Duty cycle', summaryLabel: 'duty cycle', unit: '%', kind: 'integer', min: 1, max: 100, step: 5, unreportedLabel: 'Default' },
	{ key: 'dispense_time_sec', label: 'Dispense time', summaryLabel: 'dispense time', unit: 's', kind: 'integer', min: 1, max: 60, step: 1, unreportedLabel: 'Default' },
	{ key: 'water_mixer_ml', label: 'Water', summaryLabel: 'water', unit: 'ml', kind: 'integer', min: 0, max: 250, step: 5, unreportedLabel: 'Default' },
	{ key: 'frequency_cycles', label: 'Frequency', summaryLabel: 'frequency', unit: '', kind: 'integer', min: 0, max: 10, step: 1, unreportedLabel: 'Default' }
];

export const AUGER_PURGE_MESSAGES = {
	waiting: 'Sent, waiting for the machine',
	applied: 'Applied',
	notConfirmed: 'Not confirmed. The machine may be offline.',
	unreachable: 'Could not reach the machine. Try again.',
	unknownTooltip: 'Not reported by the machine yet',
	defaultTooltip: 'No change reported yet. The machine is using its default.',
	frequencyDefaultTooltip: 'No change reported yet. The default is every 3rd cleaning cycle. 0 turns this canister off.',
	basisDefaultTooltip: 'No change reported yet. The machine default is drinks (Enable Drink Count on).',
	intervalDefaultTooltip: 'No change reported yet. With drink count on, the default is 40 drinks. With it off, the default is 180 minutes.'
};

export interface AugerPurgeCanister {
	slot: number;
	cleaning_interval: number | null;
	duty_cycle_pct: number | null;
	dispense_time_sec: number | null;
	water_mixer_ml: number | null;
	frequency_cycles: number | null;
}

export interface AugerPurgeCommand {
	command_id: string;
	requested_by: string;
	status: string;
	error: string | null;
	payload: any;
	created_at: string | null;
	sent_at: string | null;
	acked_at: string | null;
}

export interface AugerPurgeConfig {
	machine_id: string;
	enabled: boolean | null;
	cleaning_basis: string | null;
	reported_at: string | null;
	canisters: AugerPurgeCanister[];
	recent_commands: AugerPurgeCommand[];
}

export interface AugerPurgeCanisterDraft {
	slot: number;
	duty_cycle_pct: string;
	dispense_time_sec: string;
	water_mixer_ml: string;
	frequency_cycles: string;
}

export interface AugerPurgeDraft {
	enabled: 'on' | 'off' | null;
	canisters: AugerPurgeCanisterDraft[];
}

export interface AugerPurgeCanisterPatch {
	slot: number;
	duty_cycle_pct?: number;
	dispense_time_sec?: number;
	water_mixer_ml?: number;
	frequency_cycles?: number;
}

export interface AugerPurgePatch {
	machine_id: string;
	enabled?: boolean;
	canisters?: AugerPurgeCanisterPatch[];
}

export interface AugerPurgeFieldError {
	field: string;
	message: string;
}

export interface AugerPurgeEvaluation {
	patch: AugerPurgePatch | null;
	errors: AugerPurgeFieldError[];
	summary: string[];
}

export interface AugerPurgeRequestError {
	status: number;
	message: string;
	details: Array<{ field: string; message: string }>;
}

const INTEGER_PATTERN = /^(0|[1-9]\d*)$/;
const DECIMAL_PATTERN = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/;

const FIELD_MESSAGES: { [key: string]: string } = {
	duty_cycle_pct: 'Must be a whole number from 1 to 100.',
	dispense_time_sec: 'Must be a whole number of seconds from 1 to 60.',
	water_mixer_ml: 'Must be a whole number of ml from 0 to 250.',
	frequency_cycles: 'Must be a whole number from 0 to 10. 0 turns purging off for this canister.'
};

function fieldByKey(key: string): AugerPurgeField {
	for (let i = 0; i < AUGER_PURGE_FIELDS.length; i++) {
		if (AUGER_PURGE_FIELDS[i].key === key) {
			return AUGER_PURGE_FIELDS[i];
		}
	}
	return null;
}

function valueToInput(value: number | null | undefined): string {
	if (value === null || value === undefined) {
		return '';
	}
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) {
		return '';
	}
	return String(numeric);
}

export function draftFromConfig(config: AugerPurgeConfig): AugerPurgeDraft {
	const canisters = (config.canisters || []).slice().sort((a, b) => a.slot - b.slot);
	return {
		enabled: config.enabled === true ? 'on' : config.enabled === false ? 'off' : null,
		canisters: canisters.map(canister => ({
			slot: canister.slot,
			duty_cycle_pct: valueToInput(canister.duty_cycle_pct),
			dispense_time_sec: valueToInput(canister.dispense_time_sec),
			water_mixer_ml: valueToInput(canister.water_mixer_ml),
			frequency_cycles: valueToInput(canister.frequency_cycles)
		}))
	};
}

function parseField(key: AugerPurgeFieldKey, text: string): { valid: boolean; blank: boolean; value: number | null; message: string } {
	const raw = text === null || text === undefined ? '' : String(text).trim();
	if (raw === '') {
		return { valid: true, blank: true, value: null, message: '' };
	}
	const field = fieldByKey(key);
	if (field.kind === 'integer') {
		if (!INTEGER_PATTERN.test(raw)) {
			return { valid: false, blank: false, value: null, message: FIELD_MESSAGES[key] };
		}
		const value = Number(raw);
		if (value < field.min || value > field.max) {
			return { valid: false, blank: false, value: null, message: FIELD_MESSAGES[key] };
		}
		return { valid: true, blank: false, value: value, message: '' };
	}
	if (!DECIMAL_PATTERN.test(raw)) {
		return { valid: false, blank: false, value: null, message: FIELD_MESSAGES[key] };
	}
	const value = Math.round(Number(raw) * 100) / 100;
	if (value < field.min || value > field.max) {
		return { valid: false, blank: false, value: null, message: FIELD_MESSAGES[key] };
	}
	return { valid: true, blank: false, value: value, message: '' };
}

function numbersEqual(left: number, right: number, kind: 'integer' | 'decimal'): boolean {
	if (kind === 'decimal') {
		return Math.round(left * 100) === Math.round(right * 100);
	}
	return left === right;
}

export function unreportedFieldLabel(key: string): string {
	const field = fieldByKey(key);
	return field ? field.unreportedLabel : 'Unknown';
}

export function unreportedFieldTooltip(key: string): string {
	if (key === 'frequency_cycles') {
		return AUGER_PURGE_MESSAGES.frequencyDefaultTooltip;
	}
	return unreportedFieldLabel(key) === 'Default'
		? AUGER_PURGE_MESSAGES.defaultTooltip
		: AUGER_PURGE_MESSAGES.unknownTooltip;
}

export function formatFieldValue(key: string, value: number | null): string {
	if (value === null || value === undefined || !Number.isFinite(Number(value))) {
		return unreportedFieldLabel(key);
	}
	const numeric = Number(value);
	if (key === 'frequency_cycles' && numeric === 0) {
		return 'Off';
	}
	if (key === 'duty_cycle_pct') {
		return numeric + '%';
	}
	if (key === 'dispense_time_sec') {
		return numeric + ' s';
	}
	if (key === 'water_mixer_ml') {
		return numeric + ' ml';
	}
	return String(numeric);
}

export function formatEnabled(value: boolean | null): string {
	if (value === true) {
		return 'On';
	}
	if (value === false) {
		return 'Off';
	}
	return 'Default';
}

function ordinal(value: number): string {
	const mod100 = value % 100;
	if (mod100 >= 11 && mod100 <= 13) {
		return value + 'th';
	}
	switch (value % 10) {
		case 1:
			return value + 'st';
		case 2:
			return value + 'nd';
		case 3:
			return value + 'rd';
		default:
			return value + 'th';
	}
}

export function cleaningCycleHint(basis: string | null, interval: number | null, frequency: number | null): string | null {
	if (frequency === null || frequency === undefined || !Number.isFinite(Number(frequency))) {
		return null;
	}
	if (Number(frequency) === 0) {
		return 'Purging is off for this canister.';
	}
	const cycles = Number(frequency);
	if (interval === null || interval === undefined || !Number.isFinite(Number(interval))) {
		return 'Purges every ' + ordinal(cycles) + ' cleaning cycle.';
	}
	const every = Number(interval) * cycles;
	if (basis === 'drinks') {
		return 'Purges every ' + ordinal(every) + ' drink.';
	}
	if (basis === 'minutes') {
		return 'Purges every ' + every + (every === 1 ? ' minute.' : ' minutes.');
	}
	return 'Purges every ' + ordinal(every) + ' cleaning cycle.';
}

export function cleaningCycleHintForInput(basis: string | null, interval: number | null, frequencyText: string): string | null {
	const parsed = parseField('frequency_cycles', frequencyText);
	if (!parsed.valid || parsed.blank) {
		return null;
	}
	return cleaningCycleHint(basis, interval, parsed.value);
}

function reportedNumber(value: number | null | undefined): number | null {
	if (value === null || value === undefined) {
		return null;
	}
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : null;
}

export function evaluateDraft(config: AugerPurgeConfig, draft: AugerPurgeDraft): AugerPurgeEvaluation {
	const errors: AugerPurgeFieldError[] = [];
	const summary: string[] = [];
	const patch: AugerPurgePatch = { machine_id: config.machine_id };
	let changed = false;

	if (draft.enabled === null) {
		if (config.enabled === true || config.enabled === false) {
			errors.push({ field: 'enabled', message: 'Choose on or off.' });
		}
	} else {
		const nextEnabled = draft.enabled === 'on';
		if (config.enabled !== nextEnabled) {
			patch.enabled = nextEnabled;
			changed = true;
			summary.push(nextEnabled ? 'Turn purging on' : 'Turn purging off');
		}
	}

	const canisters: AugerPurgeCanisterPatch[] = [];
	(draft.canisters || []).forEach(row => {
		const reported = (config.canisters || []).filter(canister => canister.slot === row.slot)[0] || ({} as AugerPurgeCanister);
		const change: AugerPurgeCanisterPatch = { slot: row.slot };
		let canisterChanged = false;
		AUGER_PURGE_FIELDS.forEach(field => {
			const parsed = parseField(field.key, row[field.key]);
			const current = reportedNumber(reported[field.key]);
			if (!parsed.valid) {
				errors.push({ field: row.slot + '.' + field.key, message: parsed.message });
				return;
			}
			if (parsed.blank) {
				if (current !== null) {
					errors.push({ field: row.slot + '.' + field.key, message: 'Enter a value.' });
				}
				return;
			}
			if (current !== null && numbersEqual(parsed.value, current, field.kind)) {
				return;
			}
			change[field.key] = parsed.value;
			canisterChanged = true;
			summary.push(
				'canister ' + row.slot + ': ' + field.summaryLabel + ' ' +
				formatFieldValue(field.key, current) + ' → ' + formatFieldValue(field.key, parsed.value)
			);
		});
		if (canisterChanged) {
			canisters.push(change);
		}
	});

	if (canisters.length) {
		patch.canisters = canisters;
		changed = true;
	}

	return {
		patch: changed && !errors.length ? patch : null,
		errors: errors,
		summary: summary
	};
}

export function splitServerErrors(
	details: Array<{ field: string; message: string }>,
	canisters: Array<{ slot: number }>
): { fields: { [key: string]: string }; general: string } {
	const fields: { [key: string]: string } = {};
	const general: string[] = [];
	(details || []).forEach(detail => {
		if (!detail) {
			return;
		}
		const match = detail.field ? /^canisters\[(\d+)\]\.([a-z0-9_]+)$/.exec(detail.field) : null;
		if (match) {
			const canister = canisters[Number(match[1])];
			if (canister) {
				fields[canister.slot + '.' + match[2]] = detail.message;
				return;
			}
		}
		if (detail.field === 'enabled') {
			fields.enabled = detail.message;
			return;
		}
		if (detail.message) {
			general.push(detail.field ? detail.field + ': ' + detail.message : detail.message);
		}
	});
	return { fields: fields, general: general.join(' ') };
}

export function normalizeAugerPurgeError(error: any): AugerPurgeRequestError {
	const response = error && error.response;
	const data = response && response.data && typeof response.data === 'object' ? response.data : null;
	const status = (response && response.status) || (data && data.status) || 0;
	const nested = data && data.error && typeof data.error === 'object' ? data.error : null;
	const details = nested && Array.isArray(nested.details) ? nested.details : [];
	let message = (data && data.message) || (error && error.message) || 'Something went wrong.';
	if (status === 502) {
		message = AUGER_PURGE_MESSAGES.unreachable;
	} else if (status === 404) {
		message = 'This machine was not found.';
	}
	return { status: status, message: message, details: details };
}

export function describeCommandPayload(payload: any): string {
	if (!payload || typeof payload !== 'object') {
		return '—';
	}
	const parts: string[] = [];
	if (payload.enabled === true) {
		parts.push('Turn purging on');
	} else if (payload.enabled === false) {
		parts.push('Turn purging off');
	}
	const canisters = Array.isArray(payload.canisters) ? payload.canisters : [];
	canisters.forEach(canister => {
		if (!canister || canister.slot === null || canister.slot === undefined) {
			return;
		}
		const bits: string[] = [];
		AUGER_PURGE_FIELDS.forEach(field => {
			if (!Object.prototype.hasOwnProperty.call(canister, field.key)) {
				return;
			}
			bits.push(field.summaryLabel + ' ' + formatFieldValue(field.key, canister[field.key]));
		});
		if (bits.length) {
			parts.push('canister ' + canister.slot + ': ' + bits.join(', '));
		}
	});
	return parts.length ? parts.join('; ') : '—';
}

export function augerPurgeStatusLabel(status: string): string {
	if (status === 'acked') {
		return 'Applied';
	}
	if (status === 'failed') {
		return 'Failed';
	}
	if (status === 'sent' || status === 'requested') {
		return 'Sent';
	}
	return status || '—';
}

export function isPendingAugerPurgeStatus(status: string): boolean {
	return status === 'sent' || status === 'requested';
}
